import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { findAll, loginUser } from '../services/Service';
import { RootState } from '../app/store';
import {
  clearToken,
  loadingError,
  onLogin,
  onLogout,
  setRoles,
  setToken,
  visibleError,
  setLinkPermisos,
  setPermisos,
  setAuth,
  urlRedirect,
} from '../context/Auth/UserLoginReducer';
import { useAuthContext } from '../context/Auth/AuthProvider';

export const useAuth = () => {
  const dispatch = useDispatch();
  const {
    usuarioLogin,
    isAuth,
    errors,
    error,
    redirect,
    permisos,
    linkPermisos,
  } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const handlerLogin = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    try {
      console.log('INICIANDO LOGIN');
      const response = await loginUser({ username, password });
      const token = response.data.token;
      const claims = JSON.parse(window.atob(token.split('.')[1]));
      handlePermisos(claims.authorities);

      localStorage.setItem('roles', claims.authorities);
      dispatch(setRoles(true));
      localStorage.setItem('token', `Bearer ${token}`);
      dispatch(setToken(`Bearer ${token}`));

      // getUsuarioByUsername();
      await findAll(`usuarios/username`)
        .then((response) => {
          localStorage.setItem(
            'login',
            JSON.stringify({
              isAuth: true,
              usuario: response.data,
            })
          );
          dispatch(onLogin(response.data));
          if (redirect) {
            navigate(redirect);
          } else {
            navigate('/consultaSolicitudes');
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error: any) {
      if (error.response?.status === 401) {
        const errors = [error.response.data.message];
        dispatch(loadingError(errors));
      } else if (error.response?.status === 403) {
        dispatch(loadingError(error.response.data.errors));
      } else {
        throw error;
      }
    }
  };

  const { isAuthenticated, checkAuth } = useAuthContext();

  const handleCognitoAuth = async () => {
    console.log('handleCognitoAuth');
    await checkAuth();

    if (isAuthenticated) {
      console.log('Usuario autenticado');
      navigate(redirect || '/consultaSolicitudes');
    } else {
      navigate('/login');
    }
  };

  const checkAuthStatus = async () => {
    try {
      const response = await findAll(`auth/status`);
      if (response?.data?.authenticated) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const showError = (load: boolean) => {
    dispatch(visibleError(load));
  };

  const handlerLogout = async () => {
    try {
      console.log('Iniciando logout...');

      // 1. Limpiar estado de Redux inmediatamente
      dispatch(onLogout());
      dispatch(clearToken());
      dispatch(urlRedirect(null));
      dispatch(setAuth(false));
      dispatch(setRoles(false));

      // 2. Limpiar localStorage y sessionStorage
      localStorage.removeItem('token');
      localStorage.removeItem('login');
      localStorage.removeItem('roles');
      sessionStorage.removeItem('authStatus');

      console.log(
        'Estado local limpiado. Llamando al backend para logout...'
      );

      // 3. Llamar al endpoint de logout del backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/logout`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.logoutUrl) {
          console.log(
            'Redirigiendo a Cognito logout:',
            data.logoutUrl
          );
          // Usar window.location.href en lugar de replace para asegurar que funcione
          window.location.href = data.logoutUrl;
        } else {
          console.log(
            'No se recibió URL de logout, yendo al login local'
          );
          window.location.href = '/login';
        }
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (error) {
      console.error('Error durante logout:', error);
      // Fallback: construir la URL manualmente y redirigir
      const fallbackLogoutUrl = `https://grupomost.auth.sa-east-1.amazoncognito.com/logout?client_id=73oh8i7958m4jm8slla0agia5p&logout_uri=${encodeURIComponent(
        'http://localhost:3000/login'
      )}`;
      console.log('Usando fallback logout URL:', fallbackLogoutUrl);
      window.location.href = fallbackLogoutUrl;
    }
  };
  const handleRedirect = (url: string | null) => {
    dispatch(urlRedirect(url));
  };

  const handlePermisos = (
    authorities: any,
    shouldLogoutOnError = false
  ) => {
    console.log('handlePermisos', authorities);
    if (permisos && permisos.length > 0) {
      console.log('Permissions already loaded from Redux:', permisos);
      console.log('Links already loaded from Redux:', linkPermisos);
      return; // No necesita procesar, ya están cargados
    }
    const rolesPoncho = JSON.parse(authorities);
    if (Object.keys(rolesPoncho).length !== 0) {
      const auth = JSON.parse(rolesPoncho[0].authority);

      const nombres: string[] = [];
      const links: string[] = [];
      auth &&
        auth.menu.forEach((rol: { name: any; sub: any[] }) => {
          nombres.push(rol.name);
          if (rol.sub) {
            rol.sub.forEach((rolSub) => {
              nombres.push(rolSub.acro);
              links.push(rolSub.link);
            });
          }
        });

      dispatch(setPermisos(nombres));
      dispatch(setLinkPermisos(links));
    } else {
      if (shouldLogoutOnError) {
        console.log('Haciendo logout por falta de permisos');
        handlerLogout();
      } else {
        // Establecer permisos vacíos pero no hacer logout
        dispatch(setPermisos([]));
        dispatch(setLinkPermisos([]));
      }
    }
  };

  const getUsuarioLogueado = () => {
    const login = JSON.parse(localStorage.getItem('login') || '{}');
    return login?.usuario;
  };

  return {
    login: {
      usuarioLogin,
      isAuth,
    },
    handlerLogin,
    handlerLogout,
    showError,
    handlePermisos,
    getUsuarioLogueado,
    handleRedirect,
    handleCognitoAuth,
    checkAuthStatus,
    isAuth,
    errors,
    error,
    redirect,
  };
};
